import { ChoiceSaveQueue, type SavePlace } from "_shared/_api/choice-save-queue";
import type {
  ColumnVisibilityEntry,
  TableColumnPreferenceJSON,
  TableColumnPreferencesJSON,
} from "_shared/_views/tables/_schemas/table-column-preferences-schema";

export { currentPrincipal, principalOf, type SavePlace } from "_shared/_api/choice-save-queue";

/**
 * The column saves this tab has asked for and not yet had answered, one queue per table (#6151).
 *
 * Two quick switches in the Show/Hide columns menu, sent side by side, could arrive in either order
 * and keep the older choice; `ChoiceSaveQueue` explains the race and sends them one at a time. A
 * reset waits until the table's queue is empty.
 */
const tableSaves = new ChoiceSaveQueue<ColumnVisibilityEntry[]>();

export function joinSaveQueue(
  tableKey: string,
  columns: ColumnVisibilityEntry[],
  principal: string | undefined,
): SavePlace {
  return tableSaves.join(tableKey, columns, principal);
}

/** Whether a save of the table — of any table, without a key — has joined and not finished. */
export function hasPendingSave(tableKey?: string): boolean {
  return tableSaves.hasPendingSave(tableKey);
}

/** Waits until no save of the table — of any table, without a key — is waiting for its answer. */
export function untilSavesAnswered(tableKey?: string): Promise<void> {
  return tableSaves.untilSavesAnswered(tableKey);
}

/**
 * The server's answer, with each choice this person has saved and the server has not yet applied.
 *
 * An answer asked for while a save is on its way can be older than that save. Taken as it is, it
 * would put back the columns the person has just hidden, until the next answer.
 */
export function withPendingSaves(
  answer: TableColumnPreferencesJSON,
  principal: string | undefined,
): TableColumnPreferencesJSON {
  const pending = tableSaves.pendingChoices(principal);
  if (pending.length === 0) return answer;

  const tables = answer.tables.map((table) => {
    const columns = pending.find(([tableKey]) => tableKey === table.tableKey)?.[1];
    return columns ? withColumnVisibility(table, columns) : table;
  });
  for (const [tableKey, columns] of pending) {
    if (answer.tables.some((table) => table.tableKey === tableKey)) continue;
    tables.push({ tableKey, columns, updatedAt: new Date().toISOString() });
  }
  return { tables };
}

/**
 * A table's answer with `columns` as its Show/Hide columns choice, in the current view as well as in
 * the list the answer has always carried. The rest of the current view and the saved views are kept:
 * a column save changes nothing else (#6714).
 */
export function withColumnVisibility(
  table: TableColumnPreferenceJSON,
  columns: ColumnVisibilityEntry[],
): TableColumnPreferenceJSON {
  const currentView = table.currentView
    ? { ...table.currentView, state: { ...table.currentView.state, columnVisibility: columns } }
    : table.currentView;
  return { ...table, columns, currentView };
}
